import { createFileRoute, Link, useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";
import { coordParam, useNearMe } from "@/lib/use-near-me";
import { setSearchCache } from "@/lib/page-cache";
import { runProviderSearch, cleanQueryText, queryNamesLocation } from "@/lib/search-loader";
import { SearchExperience, type ResolvedCity } from "@/components/search/SearchExperience";
import { providersRouteFor, resolveCityFromLabel } from "@/lib/providers-nav";

import { Navbar } from "@/components/landing/Navbar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2 } from "lucide-react";

type SearchParams = {
 specialty?: string;
 location?: string;
 insurance?: string;
 q?: string;
 page?: number;
 pageSize?: number;
 radius?: number;
 lat?: number;
 lng?: number;
};

function SearchPendingSkeleton() {
 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />
 <header className="pt-28 pb-10 border-b border-border bg-card">
 <div className="mx-auto max-w-[1200px] px-6">
 <Skeleton className="h-4 w-24 mb-3" />
 <Skeleton className="h-8 w-2/3 max-w-md mb-4" />
 <Skeleton className="h-10 w-full max-w-xl" />
 <p className="mt-4 text-sm text-muted-foreground inline-flex items-center gap-2">
 <Loader2 className="h-4 w-4 animate-spin" /> Searching…
 </p>
 </div>
 </header>
 <div className="border-b border-border bg-card">
 <div className="mx-auto max-w-[1200px] px-6 py-3 flex flex-wrap gap-2">
 {Array.from({ length: 6 }).map((_, i) => (
 <Skeleton key={i} className="h-9 w-[120px] rounded-md" />
 ))}
 </div>
 </div>
 <main className="mx-auto max-w-[1200px] px-6 py-8 space-y-4">
 {Array.from({ length: 5 }).map((_, i) => (
 <div key={i} className="rounded-md border border-border bg-card p-6 flex gap-6">
 <Skeleton className="h-20 w-20 shrink-0 rounded-md" />
 <div className="flex-1 space-y-3">
 <Skeleton className="h-5 w-2/3" />
 <Skeleton className="h-4 w-1/2" />
 <Skeleton className="h-4 w-full" />
 <Skeleton className="h-4 w-5/6" />
 </div>
 </div>
 ))}
 </main>
 </div>
 );
}


export const Route = createFileRoute("/search")({
 validateSearch: (s: Record<string, unknown>): SearchParams => ({
 specialty: cleanQueryText(s.specialty),
 location: cleanQueryText(s.location),
 insurance: cleanQueryText(s.insurance),
 q: cleanQueryText(s.q),
 page: typeof s.page === "number" ? s.page : (typeof s.page === "string" ? Number(s.page) || undefined : undefined),
 pageSize: typeof s.pageSize === "number" ? s.pageSize : (typeof s.pageSize === "string" ? Number(s.pageSize) || undefined : undefined),
 radius: typeof s.radius === "number" ? s.radius : (typeof s.radius === "string" ? Number(s.radius) || undefined : undefined),
 lat: coordParam(s.lat, -90, 90),
 lng: coordParam(s.lng, -180, 180),
 }),
 loaderDeps: ({ search }) => ({
 specialty: search.specialty,
 location: search.location,
 insurance: search.insurance,
 q: search.q,
 radius: search.radius,
 page: search.page,
 lat: search.lat,
 lng: search.lng,
 }),
 staleTime: 60_000,
 loader: async ({ deps }) => {
  await setSearchCache();
  return runProviderSearch(deps);
 },
 pendingMs: 100,
 pendingMinMs: 300,
 pendingComponent: SearchPendingSkeleton,

 head: () => ({
 meta: [
 { title: "Search Healthcare Providers, Heallexa" },
 { name: "description", content: "Search Heallexa for doctors, dentists, therapists, and clinics by specialty, location, and insurance. AI-powered ranking." },
 { property: "og:title", content: "Search Healthcare Providers, Heallexa" },
 { property: "og:description", content: "AI-powered search for doctors, dentists, and clinics by specialty, location, and insurance." },
 { property: "og:url", content: "https://heallexa.com/search" },
 { property: "og:type", content: "website" },
 { name: "robots", content: "noindex,follow" },
 ],
 links: [{ rel: "canonical", href: "https://heallexa.com/search" }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "SearchResultsPage",
 name: "Search Healthcare Providers",
 url: "https://heallexa.com/search",
 isPartOf: { "@type": "WebSite", name: "Heallexa", url: "https://heallexa.com" },
 }),
 },
 ],
 }),
 errorComponent: ({ error }) => (
 <div className="min-h-screen flex items-center justify-center p-6 text-center">
 <div>
 <p className="text-destructive mb-4">Couldn't load results: {error.message}</p>
 <Button asChild><Link to="/">Back home</Link></Button>
 </div>
 </div>
 ),
 component: SearchPage,
});

// Thin wrapper: /search owns its own useSearch/useNavigate and cross-navigate
// policy (jump to /providers/... only when that's the sole active filter,
// otherwise stay and update its own query params) — everything else lives in
// the route-agnostic SearchExperience, shared with /providers/$id (specialty
// branch) and /providers/$state/$city.
function SearchPage() {
 const params = useSearch({ from: "/search" });
 const navigate = useNavigate({ from: "/search" });
 const loaderData = Route.useLoaderData();

 const isRouteLoading = useRouterState({
 select: (s) => s.matches.some((m) => m.routeId === "/search" && m.status === "pending"),
 });

 // "Near me": /search has no location of its own, so the visitor's coordinates
 // SCOPE it — a 25mi circle around them, with distances on every card. Skipped
 // when they typed a location, which is them saying where to search from.
 const { useMyLocation: onUseMyLocation, clearMyLocation: onClearMyLocation } = useNearMe({
  navigate,
  lat: params.lat,
  lng: params.lng,
  radiusMiles: 25,
  // A place named anywhere the visitor could name one — the location field OR
  // inside the query text ("dentists in Boston") — outranks their coordinates.
  skip: Boolean(params.location) || queryNamesLocation(params.q),
 });

 const onQueryChange = useCallback((q: string) => {
 navigate({ search: (prev: any) => ({ ...prev, q: q || undefined, page: undefined }), replace: true });
 }, [navigate]);

 const onLocationSubmit = useCallback((location: string) => {
 navigate({ search: (prev: any) => ({ ...prev, location: location || undefined, page: undefined }), replace: true });
 }, [navigate]);

 const onSpecialtyCommit = useCallback((specialty: string, currentCity: string) => {
 const resolved = currentCity !== "any" ? resolveCityFromLabel(currentCity) : null;
 const dest = providersRouteFor(specialty, resolved ?? undefined);
 if (dest) {
 navigate({ to: dest.to, params: dest.params as any, search: { page: undefined } } as any);
 } else {
 navigate({ search: (prev: any) => ({ ...prev, specialty, page: undefined }), replace: true });
 }
 }, [navigate]);

 const onCityCommit = useCallback((cityLabel: string, matched: ResolvedCity | null, currentSpecialty: string) => {
 const resolved = matched ?? resolveCityFromLabel(cityLabel);
 const dest = resolved ? providersRouteFor(currentSpecialty === "any" ? undefined : currentSpecialty, resolved) : null;
 if (dest) {
 navigate({ to: dest.to, params: dest.params as any, search: { page: undefined } } as any);
 return;
 }
 navigate({ search: (prev: any) => ({ ...prev, location: cityLabel || undefined, page: undefined }), replace: true });
 }, [navigate]);

 const onRadiusChange = useCallback((radius: number) => {
 navigate({ search: (prev: any) => ({ ...prev, radius: radius > 0 ? radius : undefined, page: undefined }), replace: true });
 }, [navigate]);

 const onPageChange = useCallback((page: number) => {
 navigate({ search: (prev: any) => ({ ...prev, page: page === 1 ? undefined : page }), replace: false });
 }, [navigate]);

 return (
 <SearchExperience
 loaderData={loaderData}
 initial={{ q: params.q ?? "", location: params.location ?? "", radius: params.radius ?? 0 }}
 isRouteLoading={isRouteLoading}
 onQueryChange={onQueryChange}
 onLocationSubmit={onLocationSubmit}
 onSpecialtyCommit={onSpecialtyCommit}
 onCityCommit={onCityCommit}
 onRadiusChange={onRadiusChange}
 onPageChange={onPageChange}
 nearMe={params.lat != null && params.lng != null}
 onUseMyLocation={onUseMyLocation}
 onClearMyLocation={onClearMyLocation}
 />
 );
}
